import { AssistantAgentSession } from '@superfunnel/types';
import { doc, setDoc } from 'firebase/firestore';
import { useCallback } from 'react';
import { db } from '../../Firebase';
import {
  BaseAgentSessionReturn,
  useBaseAgentSession,
} from '../useBaseAgentSession';

export interface UseAssistantAgentSessionReturn
  extends BaseAgentSessionReturn<AssistantAgentSession> {
  /** The structure data containing DynamicPage and Variants info */
  structureData: AssistantAgentSession['structureData'] | null;
  /** Campaign contexts attached to this session */
  campaignContexts: AssistantAgentSession['campaignContexts'];
  /** Update the structure data in Firestore */
  updateStructureData: (
    structureData: Partial<AssistantAgentSession['structureData']>,
  ) => Promise<void>;
}

/**
 * Hook for listening to assistant agent sessions in Firestore
 * Extends the base agent session hook with assistant-specific functionality:
 * - Structure data (DynamicPage + Variants)
 * - Campaign contexts
 * - Structure data updates
 *
 * @returns Session data, structure data, and update functions
 */
export function useAssistantAgentSession(): UseAssistantAgentSessionReturn {
  const baseSession = useBaseAgentSession<AssistantAgentSession>({
    // Assistant agent session doesn't have specific enable conditions
    // It relies on threadId from AgentChatContext
    enabled: true,

    // Title generation for assistant changes (variant, dynamicPage, constraint)
    getTitleForChange: (change, sessionData) => {
      const { entity, entityId, type } = change;

      switch (entity) {
        case 'dynamicPage': {
          const dpName =
            sessionData?.structureData?.dynamicPage?.name || 'Dynamic Page';
          switch (type) {
            case 'created':
              return `Created "${dpName}"`;
            case 'modified':
              return `Modified "${dpName}"`;
            case 'deleted':
              return `Deleted "${dpName}"`;
            default:
              return `Change in ${dpName}`;
          }
        }

        case 'variant': {
          const variant = sessionData?.structureData?.variants?.find(
            (v) => v.id === entityId,
          );
          const variantName = variant?.name || `Variant ${entityId}`;
          switch (type) {
            case 'created':
              return `Created variant "${variantName}"`;
            case 'modified':
              return `Modified variant "${variantName}"`;
            case 'deleted':
              return `Deleted variant "${variantName}"`;
            default:
              return `Change in variant "${variantName}"`;
          }
        }

        case 'constraint': {
          // Constraints are associated with variants
          const variantWithConstraint =
            sessionData?.structureData?.variants?.find((v) =>
              v.constraints
                ? Object.keys(v.constraints).includes(entityId)
                : false,
            );
          const variantName = variantWithConstraint?.name || 'Unknown Variant';
          switch (type) {
            case 'created':
              return `Added constraint to "${variantName}"`;
            case 'modified':
              return `Modified constraint in "${variantName}"`;
            case 'deleted':
              return `Removed constraint from "${variantName}"`;
            default:
              return 'Change in constraint';
          }
        }

        default:
          return `Change in ${entityId}`;
      }
    },

    // Sort changes: dynamicPage first, then variants, then constraints
    sortChanges: (changes) => {
      const entityOrder = { dynamicPage: 0, variant: 1, constraint: 2 };
      return [...changes].sort((a, b) => {
        const orderA = entityOrder[a.entity as keyof typeof entityOrder] ?? 3;
        const orderB = entityOrder[b.entity as keyof typeof entityOrder] ?? 3;
        return orderA - orderB;
      });
    },
  });

  // Extract structure data and campaign contexts
  const structureData = baseSession.agentSessionData?.structureData ?? null;
  const campaignContexts =
    baseSession.agentSessionData?.campaignContexts ?? undefined;

  // Function to update structure data
  const updateStructureData = useCallback(
    async (
      newStructureData: Partial<AssistantAgentSession['structureData']>,
    ): Promise<void> => {
      const { sessionDocPath, agentSessionData } = baseSession;

      if (!sessionDocPath || !agentSessionData) {
        return;
      }

      const sessionDocRef = doc(db, sessionDocPath);

      // Merge with existing structure data
      const mergedStructureData = {
        ...agentSessionData.structureData,
        ...newStructureData,
        // Deep merge for nested objects
        dynamicPage: {
          ...agentSessionData.structureData?.dynamicPage,
          ...newStructureData?.dynamicPage,
        },
        variants:
          newStructureData?.variants ??
          agentSessionData.structureData?.variants ??
          [],
      };

      await setDoc(
        sessionDocRef,
        { structureData: mergedStructureData },
        { mergeFields: ['structureData'] },
      );
    },
    [baseSession],
  );

  return {
    ...baseSession,
    structureData,
    campaignContexts,
    updateStructureData,
  };
}
